import { Component, Input, HostListener, ElementRef, Renderer2, OnDestroy, SecurityContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MarkdownModule } from 'ngx-markdown';
import { DomSanitizer } from '@angular/platform-browser';
import { marked } from 'marked';

@Component({
  imports: [CommonModule, MarkdownModule],
  standalone: true,
  selector: 'app-custom-tooltip',
  templateUrl: './custom-tooltip.component.html',
  styleUrls: ['./custom-tooltip.component.scss']
})

export class CustomTooltipComponent implements OnDestroy {
  @Input() content: string = '';
  @Input() useMarkdown: boolean = false;
  @Input() width: string = '250px';
  show = false;
  showInternal = false;
  tooltipLeft: number | null = null;
  tooltipTop: number | null = null;
  isFixed = true;
  private portalEl: HTMLElement | null = null;

  get formattedContent(): string {
    return this.content ? this.content.replace(/\n/g, '<br>') : '';
  }
  constructor(private hostRef: ElementRef, private renderer: Renderer2, private sanitizer: DomSanitizer) {}

  @HostListener('mouseenter') onMouseEnter() {
    this.setPosition();
    // try to create a portal attached to body; if successful, do not show internal tooltip
    const created = this.createPortal();
    this.show = true;
    this.showInternal = !created;
  }
  @HostListener('mouseleave') onMouseLeave() {
    this.destroyPortal();
    this.show = false;
    this.showInternal = false;
    this.tooltipLeft = null;
    this.tooltipTop = null;
  }

  private setPosition() {
    try {
      const hostEl = this.hostRef.nativeElement as HTMLElement;
      const rect = hostEl.getBoundingClientRect();
      const left = rect.left;
      const top = rect.bottom + 8;
      this.tooltipLeft = Math.max(8, left);
      this.tooltipTop = Math.max(8, top);
    } catch (e) {
      this.tooltipLeft = null;
      this.tooltipTop = null;
    }
  }

  private createPortal(): boolean {
    try {
      // remove any existing portal
      this.destroyPortal();
      const body = document.body as HTMLElement;
  const div = this.renderer.createElement('div') as HTMLDivElement;
  // add classes so component styles (including ::ng-deep .tooltip-markdown) apply
  this.renderer.addClass(div, 'tooltip-content');
  this.renderer.addClass(div, 'tooltip-markdown');
      this.portalEl = div;
      // basic styling matching .tooltip-content
      this.renderer.setStyle(div, 'position', 'fixed');
      this.renderer.setStyle(div, 'left', this.tooltipLeft !== null ? `${this.tooltipLeft}px` : '8px');
      this.renderer.setStyle(div, 'top', this.tooltipTop !== null ? `${this.tooltipTop}px` : '8px');
      this.renderer.setStyle(div, 'background', '#fff');
      this.renderer.setStyle(div, 'color', '#333');
      this.renderer.setStyle(div, 'border', '1px solid #ccc');
      this.renderer.setStyle(div, 'border-radius', '6px');
      this.renderer.setStyle(div, 'box-shadow', '0 2px 8px rgba(0,0,0,0.15)');
      this.renderer.setStyle(div, 'padding', '12px');
      this.renderer.setStyle(div, 'z-index', '20000');
      this.renderer.setStyle(div, 'min-width', this.width || '160px');
      this.renderer.setStyle(div, 'max-width', '380px');
      this.renderer.setStyle(div, 'white-space', 'normal');
      this.renderer.setStyle(div, 'text-align', 'left');
      this.renderer.setStyle(div, 'pointer-events', 'auto');

  // inject a small style block so list-spacing rules apply even when tooltip is attached to body
  const styleEl = this.renderer.createElement('style') as HTMLStyleElement;
  const css = `.tooltip-markdown ul, .tooltip-markdown ol { margin-left: 8px !important; padding-left: 12px !important; } .tooltip-markdown li { margin-bottom: 4px !important; }`;
  styleEl.textContent = css;
  this.renderer.appendChild(div, styleEl);

  // set inner content (convert markdown if required)
      let html = '';
      if (this.useMarkdown) {
        try {
          html = String(marked.parse(this.content || ''));
        } catch (e) {
          html = this.content || '';
        }
      } else {
        html = (this.formattedContent || '');
      }
  // sanitize and set - append sanitized content inside a wrapper so style block remains
  const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html) || html;
  const contentWrapper = this.renderer.createElement('div') as HTMLDivElement;
  this.renderer.addClass(contentWrapper, 'tooltip-markdown');
  contentWrapper.innerHTML = sanitized;
  this.renderer.appendChild(div, contentWrapper);
      body.appendChild(div);
      return true;
    } catch (e) {
      // fallback: do nothing
      this.portalEl = null;
      return false;
    }
  }

  private destroyPortal() {
    try {
      if (this.portalEl && this.portalEl.parentNode) {
        this.portalEl.parentNode.removeChild(this.portalEl);
      }
    } catch (e) {
      // ignore
    }
    this.portalEl = null;
  }

  get tooltipStyles(): { [key: string]: string } {
    const styles: any = {
      width: this.width || '250px',
      'text-align': 'left'
    };
    if (this.tooltipLeft !== null && this.tooltipTop !== null) {
      styles.position = 'fixed';
      styles.left = `${this.tooltipLeft}px`;
      styles.top = `${this.tooltipTop}px`;
    }
    return styles;
  }

  ngOnDestroy(): void {
    this.destroyPortal();
  }
}
